import { PrismicRichText } from '@prismicio/react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp, whenProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import LinkButton from './Link';
import HalfIconImage from './half-icon-logo-purple.png';

export const HalfLogo = styled.img.attrs({
  src: HalfIconImage.src,
  alt: '',
})`
  position: absolute;
  ${fluidProp('height', '200px', '354px')}
  right: 0;
  ${fluidProp('top', '-75px', '-125px')}
`;

export const Label = styled.div`
  margin: 0 auto 18px auto;
  text-align: center;

  & > small {
    ${({ theme }) => theme.typography.subHead}
    text-transform: uppercase;
    color: var(--colors-turquoise);
  }
`;

export const Title = styled.h2`
  ${({ theme }) => theme.typography.scale3}
  ${fluidProp(['font-size'], '24px', '42px')}
  color: var(--colors-blank);
  margin: 0 auto 18px;

  // Fallback + new way
  white-space: pre-wrap;
  white-space: break-spaces;
`;

export const Description = styled.div`
  ${({ theme }) => theme.typography.bodyLead}
  margin: 18px auto;
  color: var(--colors-moonRaker);

  a {
    color: var(--colors-turquoise);
    text-decoration: none;

    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }

  ${({ $wideContent }) => {
    switch ($wideContent) {
      case true:
        return css`
          max-width: 750px;
        `;
      case false:
      default:
        return css`
          max-width: 690px;
        `;
    }
  }}

  & > * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    color: inherit;
    margin: inherit;
  }
`;

const Icon = styled.div`
  margin: 0 auto 20px;
`;

export const SectionWrapper = styled.section`
  ${fluidProp(['padding-top', 'padding-bottom'], '150px', '130px')}
  ${fluidProp(['padding-left', 'padding-right'], '16px', '48px')}

  position: relative;
  text-align: center;

  ${({ $tone }) => {
    switch ($tone) {
      case 'zircon':
        return css`
          background-image: var(--colors-zircon);
          ${Title} {
            color: var(--colors-irisBlue);
          }
          ${Description} {
            color: var(--colors-lynch);
          }
        `;
      case 'light':
      case 'white':
        return css`
          background-image: var(--colors-blank);
          ${Title} {
            color: var(--colors-irisBlue);
          }
          ${Description} {
            color: var(--colors-bayoux);
          }
        `;
      default:
        return css`
          background-image: var(--colors-purpleMinsk);
        `;
    }
  }}

  ${whenProp(
    '$flippedIllustration',
    css`
      ${HalfLogo} {
        right: unset;
        left: 0;
        transform: scale(-1, 1);
      }
    `
  )}
  ${whenProp(
    '$noIllustration',
    css`
      ${HalfLogo} {
        display: none;
      }
    `
  )}

  ${whenProp(
    '$stackingContextIndex',
    css`
      isolation: isolate;
      z-index: ${({ $stackingContextIndex }) => +$stackingContextIndex};
    `
  )}
`;

const CTAButton = styled(LinkButton).attrs({
  withBackground: true,
  textWhite: true,
  chevron: true,
  withBorder: true,
  borderWidth: '2px',
  borderColor: 'turquoise',
})`
  && {
    ${({ theme }) => theme.typography.bodyLeadMedium}
  }
`;

const CustomCTAButton = styled(LinkButton).attrs({
  withBackground: true,
  chevron: true,
  withBorder: true,
  borderWidth: '2px',
  borderColor: 'turquoise',
})`
  && {
    ${({ theme }) => theme.typography.bodyLeadMedium}

    ${({ $tone }) => {
      switch ($tone) {
        case 'light':
        case 'white':
          return css`
            background-color: var(--colors-irisBlue);
            color: var(--colors-blank);
            border-color: var(--colors-irisBlue);
            box-shadow: 0px 6px 12px rgba(98, 77, 227, 0.3);
          `;
        case 'turquoise':
          return css`
            background-color: var(--colors-turquoise);
            color: var(--colors-irisBlue);
            border-color: var(--colors-turquoise);

            svg {
              fill: var(--colors-irisBlue);
            }
          `;
        default:
          return css`
            color: var(--colors-blank);
          `;
      }
    }}
  }
`;

function PrismicRichTextResolver({ field }) {
  if (!field) return null;
  if (typeof field === 'object') {
    return <PrismicRichText field={field} />;
  }

  return field;
}

export const PrimaryCTAButton = ({ children, ...props }) => (
  <CTAButton {...props} label={children} />
);

export const PrimaryCTACustomCTAButton = ({ children, tone, ...props }) => (
  <CustomCTAButton {...props} $tone={tone} label={children} />
);

export const PrimaryCTA = ({
  title = 'Discover how Remote can make international hiring easier.',
  description,
  label,
  children,
  position,
  tone,
  icon,
  wideContent: $wideContent,
  ...props
}) => (
  <SectionWrapper {...props} $tone={tone}>
    <HalfLogo $position={position} />
    {icon && <Icon>{icon}</Icon>}
    {label && (
      <Label>
        <small>{label}</small>
      </Label>
    )}
    <ContentContainer>
      {title && <Title>{title}</Title>}
      {description && (
        <Description $wideContent>
          <PrismicRichTextResolver field={description} />
        </Description>
      )}
      {children}
    </ContentContainer>
  </SectionWrapper>
);
